import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_ACCOUNT_TOPIC_FIELDS, SET_ACCOUNT_TOPIC_FIELDS } from '../../graphql';
import {
  AccountTopicField,
  GetAccountTopicFieldsResponse,
  TopicFieldType,
} from '../../types/graphql';
import { useAccount } from '../../context/AccountContext';

const FIELD_TYPES: { value: TopicFieldType; label: string }[] = [
  { value: 'string', label: 'Short text' },
  { value: 'text', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'enum', label: 'Enum' },
  { value: 'json', label: 'JSON object' },
  { value: 'url', label: 'URL' },
  { value: 'datetime', label: 'Date & time' },
];

function typeLabel(value: TopicFieldType) {
  return FIELD_TYPES.find((t) => t.value === value)?.label || value;
}

type DraftField = {
  fieldKey: string;
  label: string;
  description: string;
  fieldType: TopicFieldType;
  required: boolean;
  optionsText: string;
  uiGroup: string;
  sortOrder: number;
  isActive: boolean;
  isPublic: boolean;
  /** Added in this session and not yet part of the saved schema. */
  isNew?: boolean;
};

function toDraft(f: AccountTopicField, index: number): DraftField {
  let optionsText = '';
  if (f.fieldType === 'enum' && Array.isArray(f.options)) {
    optionsText = f.options.map(String).join(', ');
  } else if (f.options != null) {
    optionsText = typeof f.options === 'string' ? f.options : JSON.stringify(f.options, null, 2);
  }
  return {
    fieldKey: f.fieldKey,
    label: f.label,
    description: f.description || '',
    fieldType: (f.fieldType as TopicFieldType) || 'string',
    required: Boolean(f.required),
    optionsText,
    uiGroup: f.uiGroup || '',
    sortOrder: f.sortOrder ?? index,
    isActive: f.isActive !== false,
    isPublic: Boolean(f.isPublic),
  };
}

function emptyDraft(index: number): DraftField {
  return {
    fieldKey: '',
    label: '',
    description: '',
    fieldType: 'string',
    required: false,
    optionsText: '',
    uiGroup: '',
    sortOrder: index,
    isActive: true,
    // Private by default. A new field must be published deliberately — the
    // failure mode of forgetting is a missing field on a public page, not
    // private data (storage location, purchase price) on the internet.
    isPublic: false,
  };
}

/**
 * Account custom fields. `embedded` is the Content settings column: the
 * section title already sits on the left, so this renders only the card.
 * A row is the summary; open it to edit the full definition.
 */
export function TopicCustomFieldsSettings({ embedded = false }: { embedded?: boolean }) {
  const [fields, setFields] = useState<DraftField[]>([]);
  const [saved, setSaved] = useState('');
  const [open, setOpen] = useState<number | null>(null);
  const [openSnapshot, setOpenSnapshot] = useState<DraftField | null>(null);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const openBlockRef = useRef<HTMLDivElement | null>(null);
  const fieldsRef = useRef<DraftField[]>([]);
  const [saveMessage, setSaveMessage] = useState('');

  const { accountId, isLoading: accountsLoading } = useAccount();

  const { loading, error, data, refetch } = useQuery<GetAccountTopicFieldsResponse>(
    GET_ACCOUNT_TOPIC_FIELDS,
    { variables: { accountId }, skip: !accountId }
  );

  const [setAccountTopicFields, { loading: saving }] = useMutation(SET_ACCOUNT_TOPIC_FIELDS, {
    onCompleted: () => {
      refetch();
      setSaveMessage('Custom fields saved. Only this account will see these definitions.');
    },
    onError: (err) => {
      console.error('Error saving topic custom fields:', err);
      setSaveMessage(`Failed to save: ${err.message}`);
    },
  });

  useEffect(() => {
    if (data?.accountTopicFields) {
      const next = data.accountTopicFields.map((f, i) => toDraft(f, i));
      setFields(next);
      setSaved(JSON.stringify(next));
    }
  }, [data]);

  fieldsRef.current = fields;

  const updateField = (index: number, patch: Partial<DraftField>) => {
    setFields((prev) => {
      const next = prev.map((f, i) => (i === index ? { ...f, ...patch } : f));
      fieldsRef.current = next;
      return next;
    });
  };

  const addField = () => {
    const draft = { ...emptyDraft(fieldsRef.current.length), isNew: true };
    const next = [...fieldsRef.current, draft];
    fieldsRef.current = next;
    setFields(next);
    setOpen(next.length - 1);
    setOpenSnapshot(draft);
  };

  const removeField = (index: number) => {
    const next = fieldsRef.current.filter((_, i) => i !== index);
    fieldsRef.current = next;
    setFields(next);
  };

  const hasWork = (index: number) => {
    const field = fieldsRef.current[index];
    if (!field) return false;
    if (field.isNew) {
      return Boolean(
        field.fieldKey ||
          field.label ||
          field.description ||
          field.uiGroup ||
          field.optionsText ||
          field.required ||
          !field.isActive ||
          field.isPublic ||
          field.fieldType !== 'string'
      );
    }
    if (!openSnapshot) return false;
    return JSON.stringify(field) !== JSON.stringify(openSnapshot);
  };

  const cancelEdit = (index: number) => {
    const field = fieldsRef.current[index];
    if (field?.isNew) {
      removeField(index);
    } else if (openSnapshot && open === index) {
      const next = fieldsRef.current.map((f, i) => (i === index ? openSnapshot : f));
      fieldsRef.current = next;
      setFields(next);
    }
    setOpen(null);
    setOpenSnapshot(null);
  };

  const requestClose = (index: number) => {
    if (hasWork(index)) {
      setDiscardPrompt(true);
      return false;
    }
    cancelEdit(index);
    return true;
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  const confirmDiscard = () => {
    const index = open;
    setDiscardPrompt(false);
    if (index !== null) cancelEdit(index);
  };

  const openField = (index: number) => {
    if (open === index) {
      requestClose(index);
      return;
    }
    setOpen(index);
    setOpenSnapshot({ ...fieldsRef.current[index] });
  };

  useEffect(() => {
    if (open === null) return;
    const onPointerDown = (event: MouseEvent) => {
      if (discardPrompt) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (openBlockRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest('[data-keep-field], [data-discard-modal]'))
        return;
      if (requestCloseRef.current(open)) return;
      const swallow = (click: MouseEvent) => {
        click.preventDefault();
        click.stopPropagation();
        document.removeEventListener('click', swallow, true);
      };
      document.addEventListener('click', swallow, true);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (discardPrompt) {
        setDiscardPrompt(false);
        return;
      }
      requestCloseRef.current(open);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, fields, openSnapshot, discardPrompt]);

  const handleSave = async () => {
    if (!accountId) return;
    setSaveMessage('');
    const payload = fields.map((f, i) => {
      let options: unknown = null;
      if (f.fieldType === 'enum') {
        options = f.optionsText
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      } else if (f.fieldType === 'json' && f.optionsText.trim()) {
        try {
          options = JSON.parse(f.optionsText);
        } catch {
          throw new Error(`Field "${f.fieldKey || i + 1}": options must be valid JSON`);
        }
      }
      return {
        fieldKey: f.fieldKey.trim().toLowerCase(),
        label: f.label.trim() || f.fieldKey.trim(),
        description: f.description.trim() || null,
        fieldType: f.fieldType,
        required: f.required,
        options,
        uiGroup: f.uiGroup.trim() || null,
        sortOrder: i,
        isActive: f.isActive,
        isPublic: f.isPublic,
      };
    });

    try {
      await setAccountTopicFields({ variables: { accountId, fields: payload } });
    } catch (err) {
      if (err instanceof Error && err.message.startsWith('Field')) {
        setSaveMessage(`Failed to save: ${err.message}`);
      }
    }
  };

  const dirty = saved !== '' && JSON.stringify(fields) !== saved;
  const failed = saveMessage.startsWith('Failed');

  if (accountsLoading || loading) {
    return <p className="ink-set-empty">Loading…</p>;
  }

  if (error) {
    return <p className="ink-set-empty">Failed to load: {error.message}</p>;
  }

  if (!accountId) {
    return <p className="ink-set-empty">No account found.</p>;
  }

  return (
    <div>
      {embedded ? null : (
        <div className="flex-shrink-0 mb-6">
          <h2 className="mb-1">Custom fields</h2>
          <p className="text-muted-foreground">
            Extra structured data for each article. These are visible only to this account. Values
            are stored on each topic and validated against this schema.
          </p>
        </div>
      )}
      <div className="ink-set-card">
        {fields.length === 0 ? <p className="ink-set-empty">No custom fields yet.</p> : null}
        {fields.map((field, index) => (
          <div
            className="ink-field-block"
            key={index}
            ref={open === index ? openBlockRef : undefined}
          >
            <button
              type="button"
              className="ink-field-row"
              aria-expanded={open === index}
              onClick={() => openField(index)}
            >
              <div>
                <div className="ink-field-label">
                  {field.label || 'Untitled'}
                  <span className="ink-field-key">{field.fieldKey || 'new_field'}</span>
                  {field.isActive ? null : <span className="ink-field-key">inactive</span>}
                </div>
                {field.description ? (
                  <div className="ink-field-desc">{field.description}</div>
                ) : null}
              </div>
              <span className="ink-field-type">{typeLabel(field.fieldType)}</span>
              <span
                className="ink-field-req"
                style={{ color: field.required ? 'var(--ink-ink)' : 'var(--ink-ink3)' }}
              >
                {field.required ? 'Required' : 'Optional'}
              </span>
              <span className="ink-field-pub">{field.isPublic ? 'Public' : 'Private'}</span>
            </button>
            {open === index ? (
              <div className="ink-field-edit">
                <div className="ink-field-grid">
                  <label>
                    <span>Key</span>
                    <input
                      placeholder="pick"
                      value={field.fieldKey}
                      onChange={(e) =>
                        updateField(index, {
                          fieldKey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''),
                        })
                      }
                    />
                  </label>
                  <label>
                    <span>Label</span>
                    <input
                      placeholder="Pick payload"
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                    />
                  </label>
                  <label>
                    <span>Type</span>
                    <select
                      value={field.fieldType}
                      onChange={(e) =>
                        updateField(index, { fieldType: e.target.value as TopicFieldType })
                      }
                    >
                      {FIELD_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span>UI group</span>
                    <input
                      placeholder="Optional"
                      value={field.uiGroup}
                      onChange={(e) => updateField(index, { uiGroup: e.target.value })}
                    />
                  </label>
                </div>
                <label>
                  <span>Description</span>
                  <input
                    placeholder="Shown on the article"
                    value={field.description}
                    onChange={(e) => updateField(index, { description: e.target.value })}
                  />
                </label>
                {field.fieldType === 'enum' || field.fieldType === 'json' ? (
                  <label>
                    <span>
                      {field.fieldType === 'enum'
                        ? 'Choices, comma-separated'
                        : 'Options or schema hint, JSON'}
                    </span>
                    <textarea
                      value={field.optionsText}
                      onChange={(e) => updateField(index, { optionsText: e.target.value })}
                      placeholder={
                        field.fieldType === 'enum'
                          ? 'scheduled, live, final, postponed'
                          : '{ "schemaVersion": 1 }'
                      }
                    />
                  </label>
                ) : null}
                <div className="ink-field-checks">
                  <label>
                    <input
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => updateField(index, { required: e.target.checked })}
                    />
                    Required
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={field.isActive}
                      onChange={(e) => updateField(index, { isActive: e.target.checked })}
                    />
                    Active in editor
                  </label>
                  <label title="When on, this field can appear on your public site. Leave off for anything private.">
                    <input
                      type="checkbox"
                      checked={field.isPublic}
                      onChange={(e) => updateField(index, { isPublic: e.target.checked })}
                    />
                    Show publicly
                  </label>
                  {field.isNew ? null : (
                    <button
                      type="button"
                      className="ink-field-remove"
                      onClick={() => {
                        removeField(index);
                        setOpen(null);
                        setOpenSnapshot(null);
                      }}
                    >
                      Remove field
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        ))}
        <button type="button" className="ink-set-add" onClick={addField}>
          + Add field
        </button>
        {dirty || saveMessage ? (
          <div className="ink-set-save" data-keep-field data-error={failed ? 'true' : 'false'}>
            <span>
              {failed
                ? saveMessage
                : dirty
                  ? 'Unsaved changes. Saving replaces this account’s full schema.'
                  : saveMessage}
            </span>
            {dirty ? (
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {discardPrompt ? (
        <div
          className="ink-modal-back"
          data-discard-modal
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setDiscardPrompt(false);
          }}
        >
          <div
            className="ink-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="discard-field-title"
          >
            <div className="ink-modal-title" id="discard-field-title">
              Discard this field?
            </div>
            <div className="ink-modal-copy">You will lose what you typed.</div>
            <div className="ink-modal-actions">
              <button
                type="button"
                className="ink-btn"
                autoFocus
                onClick={() => setDiscardPrompt(false)}
              >
                Keep editing
              </button>
              <button type="button" className="ink-btn ink-btn-primary" onClick={confirmDiscard}>
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
